/**
 * Contact page — 2-step wizard + entrance animation trigger
 */

function init() {
  requestAnimationFrame(() => {
    document.body.classList.add('is-ready')
  })
}

if (document.readyState === 'complete') {
  init()
} else {
  window.addEventListener('load', init)
}

// ── Elements ───────────────────────────────────────────────────
const form = document.getElementById('contact-form')
const errorEl = document.getElementById('ct-error')
const successEl = document.getElementById('ct-success')
const stepCurrentEl = form.querySelector('.ct__step-current')
const submitBtn = form.querySelector('.ct__submit')
const submitLabel = submitBtn.querySelector('.ct__submit-text')
const backBtn = form.querySelector('.ct__back-step')

const step1El = form.querySelector('.ct__step--1')
const step2El = form.querySelector('.ct__step--2')

const nameInput = form.querySelector('#ct-name')
const companyInput = form.querySelector('#ct-company')
const emailInput = form.querySelector('#ct-email')
const reasonSelect = form.querySelector('#ct-reason')
const messageInput = form.querySelector('#ct-message')

const STEP_FIELDS = {
  1: [nameInput, companyInput, emailInput],
  2: [reasonSelect, messageInput],
}

// ── Attribution ────────────────────────────────────────────────
// Same capture shape as the freebie form: hand the server the tracker
// ids plus the UTMs off the current URL so the lead lands in Supabase
// attached to the visitor's timeline instead of floating free. Both ids
// may be null (DNT, ?notrack=1, blocked script) — the server treats them
// as optional.
function trackingContext() {
  const sp = new URLSearchParams(location.search)
  const param = (n) => sp.get(n) || null

  const cookieMatch = document.cookie.match(/(?:^|; )cft_v=([^;]*)/)
  let sessionId = null
  try { sessionId = sessionStorage.getItem('cft_s') } catch (_) {}

  let referrerHost = null
  try { referrerHost = document.referrer ? new URL(document.referrer).host : null } catch (_) {}

  return {
    visitor_id: cookieMatch ? decodeURIComponent(cookieMatch[1]) : null,
    session_id: sessionId,
    utm_source: param('utm_source'),
    utm_medium: param('utm_medium'),
    utm_campaign: param('utm_campaign'),
    utm_content: param('utm_content'),
    utm_term: param('utm_term'),
    short_link: param('cft_l'),
    referrer_host: referrerHost,
    page_path: location.pathname,
  }
}

const LABEL = {
  1: 'Continue',
  2: 'Submit',
}

let currentStep = 1

// ── Error helpers ──────────────────────────────────────────────
function showError(msg) {
  errorEl.textContent = msg
  errorEl.classList.add('is-visible')
}

function clearError() {
  errorEl.textContent = ''
  errorEl.classList.remove('is-visible')
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

// ── Step 1 / Step 2 validation ─────────────────────────────────
function validateStep1() {
  ;[nameInput, companyInput, emailInput].forEach((el) => el.classList.remove('is-invalid'))

  const name = nameInput.value.trim()
  const company = companyInput.value.trim()
  const email = emailInput.value.trim()

  if (!name) {
    nameInput.classList.add('is-invalid')
    nameInput.focus()
    showError('Please enter your name.')
    return false
  }

  if (!company) {
    companyInput.classList.add('is-invalid')
    companyInput.focus()
    showError('Please enter your company name.')
    return false
  }

  if (!email || !validateEmail(email)) {
    emailInput.classList.add('is-invalid')
    emailInput.focus()
    showError('Please enter a valid email address.')
    return false
  }

  return true
}

function validateStep2() {
  ;[reasonSelect, messageInput].forEach((el) => el.classList.remove('is-invalid'))

  if (!reasonSelect.value) {
    reasonSelect.classList.add('is-invalid')
    reasonSelect.focus()
    showError('Please select a reason to contact.')
    return false
  }

  if (!messageInput.value.trim()) {
    messageInput.classList.add('is-invalid')
    messageInput.focus()
    showError('Please add some context about your request.')
    return false
  }

  return true
}

// ── Fill-state tracker (drives submit button is-active sweep) ──
function fieldValue(el) {
  return el.value ? el.value.trim() : ''
}

function checkAllFilled() {
  const fields = STEP_FIELDS[currentStep]
  const allFilled = fields.every((el) => fieldValue(el) !== '')
  submitBtn.classList.toggle('is-active', allFilled)
}

function bindStepListeners(step) {
  const fields = STEP_FIELDS[step]
  fields.forEach((el) => {
    if (el.dataset.bound === '1') return
    el.dataset.bound = '1'
    const evt = el.tagName === 'SELECT' ? 'change' : 'input'
    el.addEventListener(evt, () => {
      el.classList.remove('is-invalid')
      clearError()
      checkAllFilled()
    })
    // Also clear invalid on input for textareas
    if (el.tagName === 'TEXTAREA') {
      el.addEventListener('input', () => {
        el.classList.remove('is-invalid')
        clearError()
        checkAllFilled()
      })
    }
  })
}

bindStepListeners(1)
bindStepListeners(2)

// ── Step transitions ───────────────────────────────────────────
function advanceToStep2() {
  clearError()
  step1El.classList.add('is-leaving')

  setTimeout(() => {
    step1El.classList.remove('is-active', 'is-leaving')
    step2El.classList.add('is-active')
    step2El.setAttribute('aria-hidden', 'false')
    step1El.setAttribute('aria-hidden', 'true')

    currentStep = 2
    stepCurrentEl.textContent = '2'
    submitLabel.textContent = LABEL[2]

    // Force reflow so the data-phase transition runs fresh
    void form.offsetWidth
    form.dataset.phase = 'step-2'

    checkAllFilled()

    // Focus the select after the cascade begins
    setTimeout(() => reasonSelect.focus({ preventScroll: true }), 220)
  }, 180)
}

function backToStep1() {
  clearError()
  step2El.classList.add('is-leaving')

  setTimeout(() => {
    step2El.classList.remove('is-active', 'is-leaving')
    step1El.classList.add('is-active')
    step1El.setAttribute('aria-hidden', 'false')
    step2El.setAttribute('aria-hidden', 'true')

    currentStep = 1
    stepCurrentEl.textContent = '1'
    submitLabel.textContent = LABEL[1]

    void form.offsetWidth
    form.dataset.phase = 'step-1-return'

    checkAllFilled()

    setTimeout(() => nameInput.focus({ preventScroll: true }), 220)
  }, 180)
}

backBtn.addEventListener('click', backToStep1)

// ── Submit handler ─────────────────────────────────────────────
form.addEventListener('submit', async (e) => {
  e.preventDefault()

  if (currentStep === 1) {
    if (!validateStep1()) return
    advanceToStep2()
    return
  }

  // Step 2: final submit
  if (!validateStep2()) return

  submitBtn.classList.add('is-loading')

  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: nameInput.value.trim(),
        company: companyInput.value.trim(),
        email: emailInput.value.trim(),
        reason: reasonSelect.value,
        message: messageInput.value.trim(),
        hp: form.querySelector('[name="hp"]')?.value || '',
        ...trackingContext(),
      }),
    })

    if (!res.ok) throw new Error(`request failed: ${res.status}`)

    form.style.display = 'none'
    document.querySelector('.ct__inner').style.display = 'none'
    successEl.classList.add('is-active')
  } catch (err) {
    showError('Something went wrong. Please try again.')
  } finally {
    submitBtn.classList.remove('is-loading')
  }
})

// Initial button state
checkAllFilled()
